import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Layers, ExternalLink, Film } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import type { AiVideoEditJobRow } from './CreativeCuts';

type OutputRow = {
  job_id?: string;
  label?: string;
  download_url?: string;
  share_url?: string;
  ok?: boolean;
};

type BatchResponse = {
  batchId: string;
  batchLabel: string | null;
  rollup: { total: number; completed: number; failed: number; inProgress: number };
  jobs: AiVideoEditJobRow[];
};

function statusBadge(status: AiVideoEditJobRow['status']) {
  switch (status) {
    case 'COMPLETED':
      return <Badge variant="success">Completed</Badge>;
    case 'FAILED':
    case 'CANCELLED':
      return <Badge variant="destructive">{status === 'FAILED' ? 'Failed' : 'Cancelled'}</Badge>;
    default:
      return <Badge variant="warning">In progress</Badge>;
  }
}

export default function CreativeCutsBatchPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<BatchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!batchId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const load = async () => {
      try {
        const res = await api.get<BatchResponse>(
          `/ai-video-edit-jobs/batches/${encodeURIComponent(batchId)}`
        );
        if (!cancelled) {
          setData(res.data);
          setError(null);
          if (res.data.rollup.inProgress === 0 && timer) {
            clearInterval(timer);
            timer = undefined;
          }
        }
      } catch (e: any) {
        const msg = e?.response?.data?.error;
        if (!cancelled) setError(typeof msg === 'string' ? msg : 'Could not load batch.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    void load();
    timer = setInterval(load, 4000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [batchId]);

  if (loading && !data) {
    return <div className="py-12 text-glass-text-2">Loading batch…</div>;
  }
  if (error && !data) {
    return (
      <div className="space-y-4 max-w-lg">
        <p className="text-red-400">{error}</p>
        <Link to="/agency/ai-video-edits" className="text-accent2 hover:underline">
          Back to AI Video Edits
        </Link>
      </div>
    );
  }
  if (!data) return null;

  const { rollup } = data;

  return (
    <div className="space-y-6 max-w-5xl">
      <button
        type="button"
        onClick={() => navigate('/agency/ai-video-edits')}
        className="inline-flex items-center gap-2 text-sm text-glass-text-2 hover:text-accent2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to AI Video Edits
      </button>

      <div>
        <h1 className="text-[2rem] font-semibold text-glass-text flex items-center gap-2">
          <Layers className="w-8 h-8 text-accent2" />
          {data.batchLabel || 'Batch AI video edits'}
        </h1>
        <p className="text-glass-text-2 mt-1 text-sm">
          {rollup.total} video{rollup.total === 1 ? '' : 's'} · {rollup.completed} completed ·{' '}
          {rollup.inProgress} in progress
          {rollup.failed > 0 ? ` · ${rollup.failed} failed` : ''}
        </p>
        {rollup.inProgress > 0 && (
          <p className="text-xs text-glass-text-3 mt-1">
            Auto-refreshing while edits render… this can take several minutes per video.
          </p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.jobs.map((job) => {
          const outputs = Array.isArray(job.outputs) ? (job.outputs as OutputRow[]) : [];
          const firstOutput = outputs.find((o) => typeof o.download_url === 'string');
          const preview = firstOutput?.download_url || job.sourcePreviewUrl || null;
          return (
            <Card key={job.id}>
              <CardContent className="p-0 overflow-hidden">
                <div className="aspect-video bg-black relative">
                  {preview ? (
                    <video
                      src={preview}
                      className="w-full h-full object-contain"
                      controls={Boolean(firstOutput)}
                      muted={!firstOutput}
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-glass-3 text-glass-text-3">
                      <Film className="w-10 h-10 opacity-60" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2">{statusBadge(job.status)}</div>
                </div>
                <div className="p-3 space-y-2 min-w-0">
                  <Link
                    to={`/agency/ai-video-edits/new?jobId=${encodeURIComponent(job.id)}`}
                    className="font-medium text-glass-text truncate block hover:text-accent2 transition-colors"
                  >
                    {job.displayName || 'AI video edit'}
                  </Link>
                  {job.errorMessage && (
                    <p className="text-xs text-red-400 line-clamp-2">{job.errorMessage}</p>
                  )}
                  {outputs.length > 0 && (
                    <div className="flex flex-wrap gap-2 text-xs">
                      {outputs.map((o, i) =>
                        typeof o.download_url === 'string' ? (
                          <a
                            key={o.job_id || i}
                            href={o.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent2 hover:underline"
                          >
                            {o.label || `Clip ${i + 1}`}
                          </a>
                        ) : null
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 pt-1">
                    <Link
                      to={`/agency/ai-video-edits/new?jobId=${encodeURIComponent(job.id)}`}
                      className="text-xs text-glass-text-2 hover:text-accent2"
                    >
                      Details
                    </Link>
                    {job.descriptEditUrl && (
                      <a
                        href={job.descriptEditUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-accent2 hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open in AI Editor
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
